import { HOKM_MATCH_LENGTHS, type HokmMatchLength } from "@bing/shared";
import type { ReactNode } from "react";
import { fa } from "../i18n/fa";
import { faDigits } from "../i18n/digits";

/**
 * حکم MATCH LENGTH — how many ROUNDS win the match (3 / 5 / 7).
 *
 * Shared by the offline start screen, the private-table settings AND the public quick-match step,
 * so the three can never offer different sets. The server clamps to this same list from the
 * TRUSTED room game, so anything the client could get wrong is refused rather than played.
 *
 * ⚠️ `label` EXISTS SO THE THREE SCREENS STAY DISTINGUISHABLE. They ask the same question and would
 * otherwise share one accessible name, and a query could not tell the quick-match step from the
 * host's settings — the exact thing تخته نرد's own quick-match picker was given its own label to
 * avoid. `options` narrows the set for a caller that offers fewer (the public queues are a SUBSET
 * of the private picker by contract, never a superset).
 */
export function HokmMatchLengthSelect({
  value,
  onChange,
  label,
  options = HOKM_MATCH_LENGTHS,
  testId,
  compact = false,
}: {
  value: HokmMatchLength;
  onChange: (value: HokmMatchLength) => void;
  /** Accessible name; defaults to the offline/private «بازی تا چند دست؟». */
  label?: string;
  /** Which lengths to offer; defaults to every one a private host may pick. */
  options?: readonly HokmMatchLength[];
  testId?: string;
  /** Start-screen sizing — see the height budget in `StartScreenShell`. */
  compact?: boolean;
}): ReactNode {
  return (
    <div
      role="radiogroup"
      aria-label={label ?? fa.hokm.chooseMatchLength}
      {...(testId ? { "data-testid": testId } : {})}
      className={`flex w-full items-stretch ${compact ? "gap-1.5" : "gap-2"}`}
    >
      {options.map((n) => {
        const active = n === value;
        return (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={fa.hokm.matchLengthOption(n)}
            onClick={() => onChange(n)}
            className={`appearance-none m-0 flex-1 cursor-pointer rounded-xl border px-2 ${
              compact ? "py-1" : "py-2.5"
            } text-center transition-transform active:scale-95 ${
              active
                ? "border-secondary bg-secondary text-ink-on-gold"
                : "border-outline-variant bg-surface-container text-on-surface"
            }`}
            style={{ fontWeight: active ? 800 : 600 }}
          >
            <span className="font-headline-sm text-headline-sm">{faDigits(String(n))}</span>
            <span className="block text-caption opacity-80">{fa.hokm.roundsUnit}</span>
          </button>
        );
      })}
    </div>
  );
}
